import React from 'react'
import PropTypes from 'prop-types'
import { ShortcutProvider, Shortcut } from '@shopify/react-shortcuts'
import {
  Popover,
  Table,
  Pane,
  majorScale,
  IconButton,
  Position
} from 'evergreen-ui'
import { compose } from 'recompose'
import { once } from 'lodash'
import SearchResults from './components/SearchResults'
import { withUserContext } from 'context/UserContext'
import { withWorkspaceContext } from 'context/WorkspaceContext'
import OmnisearchClient from 'lib/omnisearch'
import { browserHistory } from 'react-router'
import OmnisearchAnalytics from 'analytics/omnisearch'
import { debounce, values, flatten } from 'lodash'

const debouncedNavSearchTermEntered = debounce(
  OmnisearchAnalytics.navSearchTermEntered,
  500
)

const debouncedEmptySearchResults = debounce(
  OmnisearchAnalytics.navSearchResultsEmpty,
  500
)

function getAnalyticsProps({ user, workspace }, state) {
  return {
    workspaceSlug: workspace.slug,
    workspaceId: workspace.id,
    planId: workspace.billing.planId,
    userEmail: user.email,
    userId: user.id,
    searchTerm: state.searchTerm
  }
}

class Omnisearch extends React.Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    workspace: PropTypes.object.isRequired
  }

  state = {
    searchTerm: '',
    isLoading: true,
    selectedIndex: 0,
    searchResults: {}
  }

  constructor(props) {
    super(props)
    this.popover = React.createRef()
    this.popoverContent = null
    this.searchInput = null
    this.omnisearchClient = OmnisearchClient
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.selectedIndex !== this.props.selectedIndex &&
      this.popoverContent
    ) {
      const selectedElement = this.popoverContent.querySelector(
        '[data-is-selected=true]'
      )

      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'auto'
        })
      }
    }
  }

  onClickSearchResult = (category, searchIndex, item) => {
    OmnisearchAnalytics.navSearchResultClicked({
      ...getAnalyticsProps(this.props, this.state),
      navCategory: category,
      searchIndex
    })

    this.popover.current.close()
  }

  onEmptySearchResults = () => {
    if (this.state.searchTerm !== '') {
      debouncedEmptySearchResults(getAnalyticsProps(this.props, this.state))
    }
  }

  onSearch = searchTerm => {
    debouncedNavSearchTermEntered(getAnalyticsProps(this.props, this.state))
    this.setState({ searchTerm })
    this.executeSearch(searchTerm)
  }

  executeSearch = value => {
    // Strip special lunr characters here to prevent QueryParamError
    value = value
      .replace(/\+/g, '')
      .replace(/-/g, '')
      .replace(/~/g, '')

    let searchResults = {}

    if (value !== '') {
      searchResults = this.omnisearchClient.search(value)
    }

    const isEmpty = values(searchResults).every(value => value.length === 0)

    this.setState(
      {
        searchResults,
        isEmpty,
        selectedIndex: 0
      },
      () => {
        if (isEmpty) {
          this.onEmptySearchResults()
        }
      }
    )
  }

  flatResults() {
    const results = Object.values(this.state.searchResults || {})
    return flatten(results)
  }

  onNavigate = e => {
    const key = e.key
    const results = this.flatResults()

    return this.setState(prevState => {
      const isUpKey = key === 'ArrowUp' || (key === 'Tab' && e.shiftKey)
      const isDownKey = key === 'ArrowDown' || (key === 'Tab' && !e.shiftKey)

      let newIndex = prevState.selectedIndex

      if (isUpKey) {
        newIndex--

        if (newIndex < 0) {
          newIndex = results.length - 1
        }
      }

      if (isDownKey) {
        newIndex++

        if (newIndex >= results.length) {
          newIndex = 0
        }
      }

      return {
        ...prevState,
        selectedIndex: newIndex
      }
    })
  }

  onKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      this.navigateToSelectedItem()
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Tab') {
      // Do not get in the way
      e.preventDefault()
      e.persist()
      this.onNavigate(e)
    }
  }

  onOpen = () => {
    this.popover.current.open()
  }

  onClose = () => {
    this.popover.current.close()
  }

  // Select value inside search input if popover has opened
  onSelectSearchInput = () => {
    const { searchTerm } = this.state

    if (searchTerm && this.searchInput) {
      const inputField = this.searchInput.querySelector('input')

      if (inputField) {
        if (typeof inputField.select === 'function') {
          inputField.select()
        } else if (typeof inputField.setSelectionRange === 'function') {
          inputField.setSelectionRange(0, searchTerm.length)
        }
      }
    }
  }

  initOmnisearch = once(async () => {
    const { workspace, user } = this.props

    await this.omnisearchClient.init(workspace.slug, user.isAdmin)
    this.executeSearch(this.state.searchTerm)
    this.setState({ isLoading: false })
  })

  onOpenComplete = () => {
    this.initOmnisearch()
    this.onSelectSearchInput()
  }

  navigateToSelectedItem() {
    const flatResults = this.flatResults()
    const selectedElement = flatResults[this.state.selectedIndex]

    if (selectedElement) {
      this.onClose()
      browserHistory.push(selectedElement.path)
    }
  }

  render() {
    const {
      searchResults,
      searchTerm,
      isEmpty,
      isLoading,
      selectedIndex
    } = this.state

    const flatResults = this.flatResults()
    const selectedElement = flatResults[selectedIndex]

    return (
      <ShortcutProvider>
        <Shortcut ordered={['/']} onMatch={this.onOpen} />
        <Shortcut ordered={['ArrowUp']} onMatch={this.onNavigate} />
        <Shortcut ordered={['ArrowDown']} onMatch={this.onNavigate} />

        <Pane display="flex" alignItems="center">
          <Popover
            bringFocusInside
            ref={this.popover}
            statelessProps={{ elevation: 1, width: majorScale(50) }}
            position={Position.BOTTOM_RIGHT}
            content={
              <Pane innerRef={ref => (this.popoverContent = ref)}>
                <Table.Head>
                  <Table.SearchHeaderCell
                    autoFocus
                    innerRef={ref => (this.searchInput = ref)}
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={this.onSearch}
                    onKeyDown={this.onKeyDown}
                  />
                </Table.Head>

                <SearchResults
                  isLoading={isLoading}
                  isEmpty={isEmpty}
                  selectedItem={selectedElement}
                  searchTerm={searchTerm}
                  searchResults={searchResults}
                  onClickSearchResult={this.onClickSearchResult}
                />
              </Pane>
            }
            onOpenComplete={this.onOpenComplete}
          >
            <IconButton
              icon="search"
              appearance="minimal"
              iconSize={majorScale(2)}
            />
          </Popover>
        </Pane>
      </ShortcutProvider>
    )
  }
}

export default compose(
  /** @todo evaluate if we should just use the raw context consumer so we can avoid the loading spinner? */
  withWorkspaceContext,
  withUserContext
)(Omnisearch)
